// Migration: v7 → v8
// Unifies the field-level required flag to `isRequired` across the five widget
// types that historically used `required` (input text/number/email, textarea,
// radio, signature, clinicalSignature). This gives read and save-validation a
// single author flag to consult. `noteSettings[].required`, table/score-table
// constraints, and native HTML `required` are unrelated and left untouched.
//
// Also drops the obsolete `disabled` field from input widgets: it was never
// author-settable and is now driven entirely by advance-logic transient
// override, so it no longer belongs in stored data.

import type { MigratableTemplateContent } from './types';

const REQUIRED_RENAME_TYPES = new Set([
  'inputText',
  'inputNumber',
  'inputEmail',
  'textarea',
  'radio',
  'signature',
  'clinicalSignature',
]);

const INPUT_TYPES = new Set(['inputText', 'inputNumber', 'inputEmail']);

const renameRequired = (widget: Record<string, unknown>): void => {
  const type = widget.type;
  if (typeof type !== 'string' || !REQUIRED_RENAME_TYPES.has(type)) return;
  if (!('required' in widget)) return;

  widget.isRequired = widget.required;
  delete widget.required;
};

const removeInputDisabled = (widget: Record<string, unknown>): void => {
  const type = widget.type;
  if (typeof type !== 'string' || !INPUT_TYPES.has(type)) return;
  delete widget.disabled;
};

export const migrateV7ToV8 = (data: MigratableTemplateContent): void => {
  const widgets = data.widgets;
  if (widgets && typeof widgets === 'object' && !Array.isArray(widgets)) {
    for (const rawWidget of Object.values(widgets)) {
      const widget = rawWidget as Record<string, unknown>;
      renameRequired(widget);
      removeInputDisabled(widget);
    }
  }
  data.version = '8';
};
